import { z } from 'zod'

import {
  ReactionGroupSchema,
  ThreadReactionSummarySchema,
  ThreadUserSchema,
} from '../thread'
import { ReactionGroup, ThreadReactionSummary } from '../thread-reaction'
import { User } from '../user'

// Thread Comment sort field enum
export enum ThreadCommentSortFieldEnum {
  CREATED_AT = 'createdAt',
  SEQUENCE = 'sequence',
}

export enum ThreadCommentHistoryAction {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

export enum ThreadCommentVisibilityEnum {
  INTERNAL = 'INTERNAL',
  PUBLIC = 'PUBLIC',
}

/**
 * Interface representing a ThreadComment entity
 */
export interface ThreadComment {
  id: string
  _eventId?: string
  content: string
  createdAt: Date
  updatedAt?: Date
  editedAt?: Date | null
  deletedAt?: Date | null
  threadId: string
  organizationId: string
  authorId: string
  /** Server-stamped identity; absent on historical and human comments. */
  agentAccountId?: string | null
  agentAccountName?: string | null
  parentCommentId?: string | null
  clientMessageId?: string | null
  sequence: number
  // Optional: legacy persisted client objects lack it; undefined means INTERNAL
  visibility?: ThreadCommentVisibilityEnum
  // True when the author has client portal access for this organization.
  isClientAuthor?: boolean

  // Related entities
  author: Partial<User>

  // Additional computed properties
  userReaction: ThreadReactionSummary | null
  reactionGroups?: ReactionGroup[] // Slack-style grouped reactions
  totalReactions: number
  totalReplies: number
  attachmentCount: number
  hasAttachments: boolean
}

export interface ThreadCommentHistory {
  id: string
  commentId: string
  threadId: string
  editorId: string
  action: ThreadCommentHistoryAction
  previousContent?: string | null
  newContent?: string | null
  createdAt: Date
  editor?: Partial<User>
}

export interface ThreadCommentChangesMeta {
  lastCommentSequence: number
  hasMore: boolean
  serverTime: Date
}

export interface ThreadCommentChangesResponse {
  data: ThreadComment[]
  meta: ThreadCommentChangesMeta
}

// Create Thread Comment schema
export const CreateThreadCommentSchema = z.object({
  threadId: z.string().uuid(),
  parentCommentId: z.string().uuid().nullable(),
  content: z.string(),
  clientMessageId: z.string().uuid().nullable().optional(),
  // Only honored for root comments; replies inherit their parent's visibility
  visibility: z.nativeEnum(ThreadCommentVisibilityEnum).optional(),
})

export type CreateThreadComment = z.infer<typeof CreateThreadCommentSchema>

export const UpdateThreadCommentVisibilitySchema = z.object({
  visibility: z.nativeEnum(ThreadCommentVisibilityEnum),
})

export type UpdateThreadCommentVisibility = z.infer<
  typeof UpdateThreadCommentVisibilitySchema
>
